import React, { useState, useEffect, useContext } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

// My Files
import UserAvatar from "../UserAvatar";
import { UserContext, DatabaseContext } from "../../../context";

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

// Shows a list of hubs which can be added to a user
export default function ManageHubEnrolementDialog(props) {
    const { currentUserData } = useContext(UserContext);

    const { folders } = useContext(DatabaseContext);

    const [breadcrumbs, setBreadcrumbs] = useState([
        { name: "Hubs", key: "Root" },
    ]); // Used to keep track of the parent groups as user navigates down the tree, then use this to implement the breadcrumb

    const [visibleHubs, setVisibleHubs] = useState([]); // Just the hubs that are visible at the moment

    const [noHubsAtThisLevel, setNoHubsAtThisLevel] = useState(false);

    const [selectedHubs, setSelectedHubs] = useState([]);

    const [statusIsVisible, setStatusIsVisible] = useState(false);
    const [statusType, setStatusType] = useState("error");
    const [statusMessage, setStatusMessage] = useState("");

    useEffect(() => {
        if (props.open) {
            setSelectedHubs([...props.selectedHubs]);
            setBreadcrumbs([{ name: "Hubs", key: "Root" }]);

            let potentiallyVisibleHubs = folders.filter(
                (f) => f.hub && f.parent === "Root"
            );

            potentiallyVisibleHubs = potentiallyVisibleHubs.sort((a, b) =>
                a.name > b.name ? 1 : -1
            );

            if (potentiallyVisibleHubs.length === 0) {
                setNoHubsAtThisLevel(true);
            } else {
                setNoHubsAtThisLevel(false);
            }

            if (
                currentUserData &&
                currentUserData.role === "System Digital Manager"
            ) {
                setVisibleHubs(potentiallyVisibleHubs);
            } else {
                let visibleHubsArray = [];
                // Out of the hubs that could be shown at this point, check if the currentUser is a manager of any hub down the hierarchy, if so, should show that hub.
                potentiallyVisibleHubs.forEach((hub) => {
                    folders
                        .filter(
                            (f) =>
                                f.hub &&
                                (f.ancestors.includes(hub.key) ||
                                    f.key === hub.key)
                        )
                        .forEach((descendantHub) => {
                            if (
                                descendantHub.managers.includes(
                                    currentUserData.key
                                )
                            ) {
                                if (!visibleHubsArray.includes(hub)) {
                                    visibleHubsArray.push(hub);
                                }
                            }
                        });
                });

                setVisibleHubs(visibleHubsArray);
            }
        }

    }, [currentUserData, props.selectedHubs, folders, props.open]);

    const handleHubClick = (hub) => () => {
        setBreadcrumbs((prevValue) => {
            return [...prevValue, { name: hub.name, key: hub.key }];
        });

        let potentiallyVisibleHubs = folders.filter(
            (f) => f.hub && f.parent === hub.key
        );

        potentiallyVisibleHubs = potentiallyVisibleHubs.sort((a, b) =>
            a.name > b.name ? 1 : -1
        );

        if (potentiallyVisibleHubs.length === 0) {
            setNoHubsAtThisLevel(true);
        } else {
            setNoHubsAtThisLevel(false);
        }

        if (
            currentUserData &&
            currentUserData.role === "System Digital Manager"
        ) {
            setVisibleHubs(potentiallyVisibleHubs);
        } else {
            let visibleHubsArray = [];
            // Out of the hubs that could be shown at this point, check if the currentUser is a manager of any hub down the hierarchy, if so, should show that hub.
            potentiallyVisibleHubs.forEach((hub) => {
                folders
                    .filter(
                        (f) =>
                            f.hub &&
                            (f.ancestors.includes(hub.key) || f.key === hub.key)
                    )
                    .forEach((descendantHub) => {
                        if (
                            descendantHub.managers.includes(currentUserData.key)
                        ) {
                            if (!visibleHubsArray.includes(hub)) {
                                visibleHubsArray.push(hub);
                            }
                        }
                    });
            });

            setVisibleHubs(visibleHubsArray);
        }
    };

    const handleBreadcrumbClick = (breadcrumb) => () => {
        // Remove breadcrumbs after (and including) the one that has been pressed
        let index = 0;

        for (index = 0; index < breadcrumbs.length; index++) {
            let hub = breadcrumbs[index];

            if (hub.key === breadcrumb.key) {
                break;
            }
        }

        var newBreadCrumbs = [...breadcrumbs];

        newBreadCrumbs.length = index + 1;

        setBreadcrumbs(newBreadCrumbs);

        // Set the current parent to the breadcrumb that has been selected

        //
        let potentiallyVisibleHubs = folders.filter(
            (f) => f.hub && f.parent === breadcrumb.key
        );

        potentiallyVisibleHubs = potentiallyVisibleHubs.sort((a, b) =>
            a.name > b.name ? 1 : -1
        );

        if (potentiallyVisibleHubs.length === 0) {
            setNoHubsAtThisLevel(true);
        } else {
            setNoHubsAtThisLevel(false);
        }

        if (
            currentUserData &&
            currentUserData.role === "System Digital Manager"
        ) {
            setVisibleHubs(potentiallyVisibleHubs);
        } else {
            let visibleHubsArray = [];
            // Out of the hubs that could be shown at this point, check if the currentUser is a manager of any hub down the hierarchy, if so, should show that hub.
            potentiallyVisibleHubs.forEach((hub) => {
                folders
                    .filter(
                        (f) =>
                            f.hub &&
                            (f.ancestors.includes(hub.key) || f.key === hub.key)
                    )
                    .forEach((descendantHub) => {
                        if (
                            descendantHub.managers.includes(currentUserData.key)
                        ) {
                            if (!visibleHubsArray.includes(hub)) {
                                visibleHubsArray.push(hub);
                            }
                        }
                    });
            });

            setVisibleHubs(visibleHubsArray);
        }
    };

    const handleDone = () => {
        props.onClose(selectedHubs);
    };

    const handleCancel = () => {
        props.onClose();
    };

    const handleAddToHubClick = (addingHub) => () => {
        let childHubs = folders.filter(
            (f) => f.hub && f.ancestors.includes(addingHub.key)
        );

        let newSelectedHubs = [...selectedHubs, addingHub];

        childHubs.forEach((childHub) => {
            if (
                selectedHubs.filter((h) => h.key === childHub.key).length === 0
            ) {
                newSelectedHubs.push(childHub);
            }
        });

        setSelectedHubs(newSelectedHubs);
    };

    const handleRemoveFromHubClick = (removingHub) => () => {
        var shouldRemove = true;
        removingHub.ancestors.forEach((ancestorKey) => {
            if (selectedHubs.filter((h) => h.key === ancestorKey).length > 0) {
                shouldRemove = false;
            }
        });

        if (!shouldRemove) {
            showStatusMessage(
                "The user is a member of a hub higher in the hierarchy",
                "error"
            );

            return;
        }

        let childHubs = folders.filter(
            (f) => f.hub && f.ancestors.includes(removingHub.key)
        );

        let newSelectedHubs = [];

        selectedHubs.forEach((selectedHub) => {
            if (selectedHub.key !== removingHub.key) {
                if (
                    childHubs.filter((h) => h.key === selectedHub.key)
                        .length === 0
                ) {
                    newSelectedHubs.push(selectedHub);
                }
            }
        });

        setSelectedHubs(newSelectedHubs);
    };

    function shouldAddRemoveButtonBeDisabled(hub) {
        // If the user is not in the managers of the folder (and they are not a System Digital Manager)
        // The button should be disabled

        if (!hub.managers.includes(currentUserData.key)) {
            if (currentUserData.role !== "System Digital Manager") {
                return true;
            }
        }

        return false;
    }

    const handleStatusClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }

        setStatusIsVisible(false);
    };

    function showStatusMessage(message, type) {
        setStatusType(type);
        setStatusMessage(message);
        setStatusIsVisible(true);
    }

    return (
        <Dialog
            fullScreen
            onClose={handleCancel}
            aria-labelledby="manage-hubs-dialog-title"
            open={props.open}
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: "background.default",
                    },
                },
            }}
        >
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                            ? "0 2px 12px rgba(0, 0, 0, 0.3)"
                            : "0 2px 12px rgba(20, 35, 25, 0.06)",
                }}
            >
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "action.hover",
                    }}
                >
                    <AccountTreeOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                        }}
                    >
                        <Typography
                            id="manage-hubs-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Hub memberships
                        </Typography>
                        <Chip
                            size="small"
                            label={`${selectedHubs.length} ${
                                selectedHubs.length === 1 ? "hub" : "hubs"
                            } selected`}
                            sx={{
                                height: 24,
                                backgroundColor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.11),
                                color: "primary.main",
                                fontWeight: 700,
                            }}
                        />
                    </Box>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 0.4,
                            display: { xs: "none", sm: "block" },
                        }}
                    >
                        Browse the hierarchy and choose the user’s hubs.
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                    <Button color="inherit" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<CheckIcon />}
                        onClick={handleDone}
                    >
                        Apply to draft
                    </Button>
                </Box>
            </Box>

            <DialogContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Container maxWidth="md" disableGutters>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.25,
                            mb: 2,
                            p: { xs: 1.5, sm: 2 },
                            borderRadius: 2.5,
                            color: "text.secondary",
                            border: "1px solid",
                            borderColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.18),
                            backgroundColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.07),
                        }}
                    >
                        <InfoOutlinedIcon
                            sx={{
                                mt: 0.1,
                                flexShrink: 0,
                                color: "primary.main",
                            }}
                        />
                        <Box>
                            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                                Adding the user to a hub also adds them to every
                                hub below it in the hierarchy.
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ mt: 0.5, lineHeight: 1.55 }}
                            >
                                A user cannot be removed from a hub while they
                                belong to one of its parent hubs.
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ mt: 0.5, lineHeight: 1.55, fontWeight: 700 }}
                            >
                                These selections are not saved until you press
                                Save changes on the user page.
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            overflow: "hidden",
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            boxShadow: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "0 8px 28px rgba(0, 0, 0, 0.3)"
                                    : "0 8px 28px rgba(20, 35, 25, 0.075)",
                        }}
                    >
                    <Breadcrumbs
                        separator="›"
                        aria-label="Hub hierarchy"
                        sx={{
                            px: { xs: 2, sm: 2.5 },
                            py: 1.75,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.subtle",
                        }}
                    >
                        {breadcrumbs.map((breadcrumb) => (
                            <Link
                                component="button"
                                type="button"
                                onClick={handleBreadcrumbClick(breadcrumb)}
                                key={breadcrumb.key}
                                underline="hover"
                                sx={{
                                    border: 0,
                                    p: 0,
                                    font: "inherit",
                                    cursor: "pointer",
                                    background: "none",
                                }}
                            >
                                {breadcrumb.name}
                            </Link>
                        ))}
                    </Breadcrumbs>

                    {noHubsAtThisLevel || visibleHubs.length === 0 ? (
                        <Box
                            sx={{
                                minHeight: 260,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 3,
                                textAlign: "center",
                                color: "text.secondary",
                            }}
                        >
                            <AccountTreeOutlinedIcon
                                sx={{ fontSize: 44, mb: 1, opacity: 0.35 }}
                            />
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: "text.primary",
                                    fontWeight: 800,
                                }}
                            >
                                No hubs at this level
                            </Typography>
                            <Typography variant="body2">
                                Use the breadcrumb trail to return to a parent
                                hub.
                            </Typography>
                        </Box>
                    ) : (
                        <List
                            disablePadding
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                p: { xs: 1.5, sm: 2 },
                            }}
                        >
                        {visibleHubs.map((hub) => {
                            const isSelected = selectedHubs.some(
                                (selectedHub) => selectedHub.key === hub.key,
                            );
                            const isDisabled =
                                shouldAddRemoveButtonBeDisabled(hub);

                            return (
                            <React.Fragment key={hub.key}>
                                <ListItem
                                    disablePadding
                                    sx={{
                                        overflow: "hidden",
                                        borderRadius: 2.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        backgroundColor: "background.paper",
                                    }}
                                >
                                    <ListItemButton
                                        onClick={handleHubClick(hub)}
                                        sx={{
                                            minWidth: 0,
                                            py: 1.25,
                                            pl: 1.5,
                                            pr: 1,
                                            "&:hover": {
                                                backgroundColor:
                                                    "action.hover",
                                            },
                                        }}
                                    >
                                        <ListItemAvatar
                                            sx={{ minWidth: 58 }}
                                        >
                                            <UserAvatar
                                                userOrHub={hub}
                                                path="hubs"
                                                variant="rounded"
                                                size="medium"
                                            />
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={hub.name}
                                            secondary={
                                                isDisabled
                                                    ? "You are not a manager of this hub"
                                                    : "Open child hubs"
                                            }
                                            primaryTypographyProps={{
                                                sx: { fontWeight: 800 },
                                            }}
                                        />
                                        <ChevronRightIcon
                                            sx={{
                                                ml: 0.5,
                                                color: "text.disabled",
                                            }}
                                        />
                                    </ListItemButton>
                                    <Tooltip
                                        title={
                                            isSelected
                                                ? "Remove user from hub"
                                                : "Add user to hub"
                                        }
                                    >
                                        <span>
                                            {isSelected ? (
                                                <IconButton
                                                    onClick={handleRemoveFromHubClick(
                                                        hub,
                                                    )}
                                                    color="secondary"
                                                    disabled={isDisabled}
                                                    aria-label={`Remove user from ${hub.name}`}
                                                    sx={{ mr: 1 }}
                                                >
                                                    <RemoveCircleOutlineIcon />
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    onClick={handleAddToHubClick(
                                                        hub,
                                                    )}
                                                    color="primary"
                                                    disabled={isDisabled}
                                                    aria-label={`Add user to ${hub.name}`}
                                                    sx={{ mr: 1 }}
                                                >
                                                    <AddCircleOutlineIcon />
                                                </IconButton>
                                            )}
                                        </span>
                                    </Tooltip>
                                </ListItem>
                            </React.Fragment>
                            );
                        })}
                        </List>
                    )}
                    </Box>
                </Container>
            </DialogContent>
            <Snackbar
                open={statusIsVisible}
                autoHideDuration={6000}
                onClose={handleStatusClose}
            >
                <Alert onClose={handleStatusClose} severity={statusType}>
                    {statusMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}
